import { useState, type ReactNode } from 'react';

import type { Session } from '../api';
import { ROUTES, GROUP_LABELS, isRouteVisible, activeRouteId, type RouteEntry } from '../routes';
import { UserAvatar } from './UserAvatar';

/* ── Persist sidebar state ──────────────────────────────────────────── */

function useSidebarState() {
  const [expanded, setExpanded] = useState(() => {
    try {
      return localStorage.getItem('sidebar-expanded') !== 'false';
    } catch {
      return true;
    }
  });
  const toggle = () => {
    const next = !expanded;
    setExpanded(next);
    try {
      localStorage.setItem('sidebar-expanded', String(next));
    } catch {
      /* ignore */
    }
  };
  return { expanded, toggle };
}

function useGroupState(groupId: string) {
  const key = `sidebar-group-${groupId}`;
  const [open, setOpen] = useState(() => {
    try {
      return localStorage.getItem(key) !== 'false';
    } catch {
      return true;
    }
  });
  const toggle = () => {
    const next = !open;
    setOpen(next);
    try {
      localStorage.setItem(key, String(next));
    } catch {
      /* ignore */
    }
  };
  return { open, toggle };
}

/* ── Icons (heroicons-style, 20×20 viewBox) ────────────────────────── */

const ICON: Record<string, ReactNode> = {
  operations: (
    <svg
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      className="h-5 w-5"
    >
      <rect x="2" y="2" width="7" height="7" rx="1.5" />
      <rect x="11" y="2" width="7" height="7" rx="1.5" />
      <rect x="2" y="11" width="7" height="7" rx="1.5" />
      <rect x="11" y="11" width="7" height="7" rx="1.5" />
    </svg>
  ),
  executive: (
    <svg
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      className="h-5 w-5"
    >
      <rect x="3" y="12" width="3" height="5" rx="0.5" />
      <rect x="8.5" y="7" width="3" height="10" rx="0.5" />
      <rect x="14" y="3" width="3" height="14" rx="0.5" />
    </svg>
  ),
  clients: (
    <svg
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      className="h-5 w-5"
    >
      <path d="M3 17V5a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v12" />
      <path d="M13 9h2a2 2 0 0 1 2 2v6" />
      <path d="M3 17h14" />
      <line x1="6" y1="6" x2="8" y2="6" />
      <line x1="6" y1="9" x2="8" y2="9" />
      <line x1="6" y1="12" x2="8" y2="12" />
    </svg>
  ),
  contractors: (
    <svg
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      className="h-5 w-5"
    >
      <circle cx="10" cy="5.5" r="3" />
      <path d="M4 17c0-3 2.5-5.5 6-5.5s6 2.5 6 5.5" />
      <path d="M7.5 10 10 12.5 12.5 10" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  recruiters: (
    <svg
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      className="h-5 w-5"
    >
      <circle cx="7" cy="6" r="2.5" />
      <path d="M2 16c0-2.5 2-4.5 5-4.5s5 2 5 4.5" />
      <circle cx="14" cy="7" r="2" />
      <path d="M14.5 11.5c2 0 3.5 1.3 3.5 3" />
    </svg>
  ),
  credits: (
    <svg
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      className="h-5 w-5"
    >
      <path d="M3 5h14M3 10h14M3 15h14" strokeLinecap="round" />
      <circle cx="15" cy="5" r="1.5" fill="currentColor" stroke="none" />
      <circle cx="15" cy="10" r="1.5" fill="currentColor" stroke="none" />
    </svg>
  ),
  'my-commissions': (
    <svg
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      className="h-5 w-5"
    >
      <circle cx="10" cy="10" r="7" />
      <path d="M10 6v4l2.5 2.5" strokeLinecap="round" />
      <path d="M7.5 13.5 10 10" strokeLinecap="round" />
    </svg>
  ),
  'gp-summary': (
    <svg
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      className="h-5 w-5"
    >
      <path d="M3 3v14h14" strokeLinecap="round" />
      <path d="M6 14l3-4 3 2 4-6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  'commission-report': (
    <svg
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      className="h-5 w-5"
    >
      <rect x="3" y="2" width="14" height="16" rx="1.5" />
      <line x1="6" y1="6" x2="14" y2="6" />
      <line x1="6" y1="9" x2="14" y2="9" />
      <line x1="6" y1="12" x2="10" y2="12" />
    </svg>
  ),
  scoreboard: (
    <svg
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      className="h-5 w-5"
    >
      <path d="M10 2l2 4 4.5.5-3.3 3 1 4.5L10 12l-4.2 2 1-4.5-3.3-3L8 6z" strokeLinejoin="round" />
    </svg>
  ),
  expenses: (
    <svg
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      className="h-5 w-5"
    >
      <circle cx="10" cy="10" r="7" />
      <path d="M10 6v8M7.5 8.5h5a1.5 1.5 0 0 1 0 3H7.5" strokeLinecap="round" />
    </svg>
  ),
  docs: (
    <svg
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      className="h-5 w-5"
    >
      <path d="M4 2h8l4 4v12a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1Z" />
      <path d="M12 2v4h4" />
      <line x1="6" y1="10" x2="14" y2="10" />
      <line x1="6" y1="13" x2="12" y2="13" />
    </svg>
  ),
  admin: (
    <svg
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      className="h-5 w-5"
    >
      <circle cx="10" cy="10" r="3" />
      <path d="M10 1.5v2M10 16.5v2M18.5 10h-2M3.5 10h-2M16 4l-1.4 1.4M5.4 14.6 4 16M16 16l-1.4-1.4M5.4 5.4 4 4" />
    </svg>
  ),
  signout: (
    <svg
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      className="h-5 w-5"
    >
      <path d="M13 3h3a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1h-3" />
      <path d="M9 14l4-4-4-4" />
      <line x1="13" y1="10" x2="3" y2="10" />
    </svg>
  ),
};

function getIcon(id: string): ReactNode {
  return ICON[id] ?? ICON['operations']!;
}

/* ── Nav item ───────────────────────────────────────────────────────── */

function NavItem({
  route,
  active,
  expanded,
  navigate,
  indent,
}: {
  route: RouteEntry;
  active: boolean;
  expanded: boolean;
  navigate: (hash: string) => void;
  indent?: boolean;
}) {
  const icon = getIcon(route.icon ?? route.id);

  return (
    <div className="group relative">
      <button
        type="button"
        onClick={() => navigate(route.hash)}
        className={`flex w-full items-center gap-3 rounded-lg text-sm font-medium transition-colors ${
          indent ? 'px-3 py-1.5 pl-9' : 'px-3 py-2'
        } ${
          active
            ? 'border-l-2 border-profit bg-white/10 text-white'
            : 'border-l-2 border-transparent text-slate-400 hover:bg-white/5 hover:text-slate-200'
        }`}
      >
        {!indent && <span className="shrink-0">{icon}</span>}
        {indent && <span className="shrink-0 w-1.5 h-1.5 rounded-full bg-current opacity-40" />}
        {expanded && <span className="truncate">{route.label}</span>}
      </button>
      {!expanded && (
        <div className="pointer-events-none absolute left-full top-1/2 z-50 ml-2 -translate-y-1/2 rounded-md bg-slate-800 px-2 py-1 text-xs text-slate-200 opacity-0 shadow-lg transition-opacity group-hover:opacity-100">
          {route.label}
        </div>
      )}
    </div>
  );
}

/* ── Group header ───────────────────────────────────────────────────── */

function GroupHeader({
  label,
  expanded: sidebarExpanded,
  open,
  onToggle,
}: {
  label: string;
  expanded: boolean;
  open: boolean;
  onToggle: () => void;
}) {
  if (!sidebarExpanded) return null;
  return (
    <button
      type="button"
      onClick={onToggle}
      className="flex w-full items-center gap-2 px-3 py-1.5 mt-3 mb-0.5"
    >
      <svg
        viewBox="0 0 20 20"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        className={`h-3 w-3 text-slate-500 transition-transform duration-150 ${open ? 'rotate-90' : ''}`}
      >
        <path d="M7 4l6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-slate-500">
        {label}
      </span>
    </button>
  );
}

/* ── Sidebar ────────────────────────────────────────────────────────── */

export function Sidebar({
  session,
  currentRouteId,
  navigate,
  onSignOut,
}: {
  session: Session;
  currentRouteId: string;
  navigate: (hash: string) => void;
  onSignOut: () => void;
}) {
  const { expanded, toggle } = useSidebarState();
  const reportsGroup = useGroupState('reports');
  const workGroup = useGroupState('work');

  const visibleRoutes = ROUTES.filter((r) => isRouteVisible(r, session));
  const mainRoutes = visibleRoutes.filter((r) => r.section === 'main');
  const bottomRoutes = visibleRoutes.filter((r) => r.section === 'bottom');

  // Split main routes into: ungrouped (top), work group, reports group, ungrouped (bottom)
  const ungroupedTop = mainRoutes.filter((r) => !r.group);
  const workRoutes = mainRoutes.filter((r) => r.group === 'work');
  const reportRoutes = mainRoutes.filter((r) => r.group === 'reports');

  // Separate standalone items: My Commissions goes before groups, Executive after
  const topStandalone = ungroupedTop.filter((r) => r.id === 'my-commissions');
  const bottomStandalone = ungroupedTop.filter((r) => r.id !== 'my-commissions');

  const resolvedActive = activeRouteId(currentRouteId);

  return (
    <aside
      className="print:hidden flex h-screen flex-col border-r border-slate-800 bg-slate-900 text-slate-300 transition-[width] duration-300 ease-in-out"
      style={{ width: expanded ? 240 : 64 }}
    >
      {/* Header */}
      <div className="flex h-14 shrink-0 items-center gap-3 border-b border-slate-800 px-4">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-profit text-white text-xs font-bold">
          R
        </div>
        {expanded && (
          <div className="flex flex-col leading-tight">
            <span className="text-sm font-semibold text-white">RMC</span>
            <span className="text-[11px] text-slate-500">Payroll</span>
          </div>
        )}
      </div>

      {/* Main nav */}
      <nav className="flex-1 space-y-0.5 overflow-y-auto px-2 py-3">
        {/* Top standalone items (My Commissions for recruiters) */}
        {topStandalone.map((r) => (
          <NavItem
            key={r.id}
            route={r}
            active={resolvedActive === r.id}
            expanded={expanded}
            navigate={navigate}
          />
        ))}

        {/* Work group */}
        {workRoutes.length > 0 && (
          <>
            <GroupHeader
              label={GROUP_LABELS['work']!}
              expanded={expanded}
              open={workGroup.open}
              onToggle={workGroup.toggle}
            />
            {(workGroup.open || !expanded) &&
              workRoutes.map((r) => (
                <NavItem
                  key={r.id}
                  route={r}
                  active={resolvedActive === r.id}
                  expanded={expanded}
                  navigate={navigate}
                  indent={expanded}
                />
              ))}
          </>
        )}

        {/* Reports group */}
        {reportRoutes.length > 0 && (
          <>
            <GroupHeader
              label={GROUP_LABELS['reports']!}
              expanded={expanded}
              open={reportsGroup.open}
              onToggle={reportsGroup.toggle}
            />
            {(reportsGroup.open || !expanded) &&
              reportRoutes.map((r) => (
                <NavItem
                  key={r.id}
                  route={r}
                  active={resolvedActive === r.id}
                  expanded={expanded}
                  navigate={navigate}
                  indent={expanded}
                />
              ))}
          </>
        )}

        {/* Bottom standalone items (Executive Dashboard) */}
        {bottomStandalone.length > 0 && expanded && <div className="mt-3" />}
        {bottomStandalone.map((r) => (
          <NavItem
            key={r.id}
            route={r}
            active={resolvedActive === r.id}
            expanded={expanded}
            navigate={navigate}
          />
        ))}
      </nav>

      {/* Bottom section */}
      <div className="border-t border-slate-800 px-2 py-3 space-y-1">
        {bottomRoutes.map((r) => (
          <NavItem
            key={r.id}
            route={r}
            active={resolvedActive === r.id}
            expanded={expanded}
            navigate={navigate}
          />
        ))}

        {/* User info */}
        <div className="group relative flex items-center gap-3 rounded-lg px-3 py-2">
          <span className="shrink-0">
            <UserAvatar name={session.user.fullName} size="sm" />
          </span>
          {expanded && (
            <div className="min-w-0 flex-1">
              <p className="truncate text-xs font-medium text-slate-200">{session.user.fullName}</p>
              <p className="truncate text-[11px] capitalize text-slate-500">
                {session.user.role.replace('_', ' ')}
              </p>
            </div>
          )}
          {!expanded && (
            <div className="pointer-events-none absolute left-full top-1/2 z-50 ml-2 -translate-y-1/2 rounded-md bg-slate-800 px-2 py-1 text-xs text-slate-200 opacity-0 shadow-lg transition-opacity group-hover:opacity-100">
              {session.user.fullName}
            </div>
          )}
        </div>

        {/* Sign out */}
        <div className="group relative">
          <button
            type="button"
            onClick={onSignOut}
            className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-slate-400 transition-colors hover:bg-white/5 hover:text-slate-200 border-l-2 border-transparent"
          >
            <span className="shrink-0">{ICON['signout']}</span>
            {expanded && <span>Sign out</span>}
          </button>
          {!expanded && (
            <div className="pointer-events-none absolute left-full top-1/2 z-50 ml-2 -translate-y-1/2 rounded-md bg-slate-800 px-2 py-1 text-xs text-slate-200 opacity-0 shadow-lg transition-opacity group-hover:opacity-100">
              Sign out
            </div>
          )}
        </div>

        {/* Toggle button */}
        <button
          type="button"
          onClick={toggle}
          className="flex w-full items-center justify-center rounded-lg py-2 text-slate-500 transition-colors hover:bg-white/5 hover:text-slate-300"
          aria-label={expanded ? 'Collapse sidebar' : 'Expand sidebar'}
        >
          <svg
            viewBox="0 0 20 20"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            className="h-5 w-5"
          >
            {expanded ? <path d="M12.5 4l-5 6 5 6" /> : <path d="M7.5 4l5 6-5 6" />}
          </svg>
        </button>
      </div>
    </aside>
  );
}
